import { EventEmitter } from './eventEmitter';

//
// Events:
//  - connect-video
//  - connect-data
//  - disconnected
//  - data
//  - iceCandidate
//  - muted
//  - unmuted
//  - failed
//  - ice-failed
//
export class WebRTC extends EventEmitter {
    pc: RTCPeerConnection | null = null;
    dataChannel: RTCDataChannel | null = null;

    constructor(turnUsername: string, turnPassword: string) {
        super();
        
        const config: RTCConfiguration = {
            //bundlePolicy?: RTCBundlePolicy;
            //certificates?: RTCCertificate[];
            //iceCandidatePoolSize?: number;
            iceServers: [
                {
                    urls: [
                        //'stun:stun.l.google.com:19302',
                        'stun:stun1.l.google.com:19302',
                        'stun:stun2.l.google.com:19302',
                        //'stun:stun3.l.google.com:19302',
                        //'stun:stun4.l.google.com:19302',
                    ],
                },
            ],
            //iceTransportPolicy?: RTCIceTransportPolicy;
            //peerIdentity?: string;
            //rtcpMuxPolicy?: RTCRtcpMuxPolicy;
        };

        if (turnUsername && turnPassword) {
            config.iceServers!.push({
                urls: [
                    'turn:turn.mosaicone.net:443?transport=udp',
                    'turn:turn.mosaicone.net:443?transport=tcp',
                ],
                username: turnUsername,
                credential: turnPassword,
            });
        }

        try {
            this.pc = new RTCPeerConnection(config);
        }
        catch (err) {
            console.error(`Failed to create RTC peer connection with config ${JSON.stringify(config, null, 2)}`);
            throw new Error('ERROR_WEBRTC');
        }
        const pc = this.pc;

        this.pc.onconnectionstatechange = () => {
            console.log(`Connection state: ${pc.connectionState}`);
            if (pc.connectionState === 'failed') {
                this.emit('failed');
            }
            else if (pc.connectionState === 'disconnected') {
                this.emit('disconnected');
            }
        };

        this.pc.oniceconnectionstatechange = () => {
            console.log(`ICE connection state: ${pc.iceConnectionState}`);
            if (pc.iceConnectionState === 'failed') {
                this.emit('ice-failed');
            }
        };

        this.pc.onicegatheringstatechange = () => {
            console.log(`ICE gathering state: ${pc.iceGatheringState}`);
        };
        this.pc.onicecandidateerror = ev => {
            // TODO: remove this once the type definition has been fixed
            // HACK: lib.dom.d.ts declares the parameter as type Event even though MDN defines it as RTCPeerConnectionIceErrorEvent:
            // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/icecandidateerror_event
            const e = ev as RTCPeerConnectionIceErrorEvent;
            // Note: hostCandidate was deprecated... but we keep it here for backwards compatibility
            const hostCandidate = (e as any).hostCandidate || `${e.address}:${e.port}`;
            if (hostCandidate.startsWith('[0:0:0:x:x:x:x:x]')) {
                // Not using IPv6
                return;
            }
            console.error(`ICE candidate error ${e.errorCode}: ${e.errorText}. (${e.url} - ${hostCandidate})`);
        };

        this.pc.onnegotiationneeded = () => {
            console.warn('Negotiation needed');
        };
    
        this.pc.onicecandidate = e => {
            console.log('onicecandidate', e.candidate);
            this.emit('iceCandidate', e.candidate);
        };

        this.pc.ontrack = e => {
            console.log('ontrack', e);
            const { track } = e;
            track.onunmute = () => {
                this.emit('unmuted', track);
            };
            track.onmute = () => {
                this.emit('muted', track);
            };

            const stream = e.streams[0];
            this.emit('connect-video', stream);
        };

        this.pc.ondatachannel = e => {
            console.log('Data channel created');
            this.dataChannel = e.channel;
            this.dataChannel.onopen = () => {
                this.emit('connect-data');
            };
            this.dataChannel.onmessage = e => {
                console.log(`Received message from elevator: ${e.data}`);
                this.emit('data', e.data);
            };
        };
    }

    private sendToElevator(data: Record<string, unknown>) {
        if (this.dataChannel && this.dataChannel.readyState === 'open') {
            const payload = JSON.stringify(data);
            console.log(`Sending payload to elevator: ${payload}`);
            try {
                this.dataChannel.send(payload);
                return;
            }
            catch (err) {
                console.error(err);
            }
        }
        throw new Error('DATA_CHANNEL_NOT_OPEN');
    }

    sendMessage(message: string, messageId: string) {
        this.sendToElevator({ message, messageId });
    }

    sendHelpIsOnTheWay() {
        this.sendToElevator({ helpIsOnTheWay: true });
    }

    sendCallEnded() {
        this.sendToElevator({ type: 'CALL_ENDED' });
    }

    async negotiateSession(offer: string) {
        if (!offer || !this.pc) {
            throw new Error('No offer to accept');
        }

        console.debug('offer', offer);
        await this.pc.setRemoteDescription({
            sdp: offer,
            type: 'offer',
        } as RTCSessionDescription);

        const answer = await this.pc.createAnswer({
            offerToReceiveAudio: false,
            offerToReceiveVideo: true,
        } as any); // TODO: fix when TypeScript is updated

        await this.pc.setLocalDescription(answer);
        return answer;
    }

    async addRemoteCandidates(candidates: ReadonlyArray<RTCIceCandidate>) {
        if (!candidates || !this.pc) {
            return;
        }

        console.log('Received candidates:', candidates);
        for (const candidate of candidates) {
            if (candidate) {
                await this.pc.addIceCandidate(candidate);
            }
        }

        // TODO: how to signal end of ICE gathering? these all fail
        //await this.pc.addIceCandidate({ candidate: '', sdpMid: 'video', sdpMLineIndex: 0 });
        //await this.pc.addIceCandidate({ candidate: '', sdpMid: '', sdpMLineIndex: 0 });
        //await this.pc.addIceCandidate({ candidate: '' });
        //await this.pc.addIceCandidate({ candidate: null, sdpMid: '', sdpMLineIndex: 0 });
        //await this.pc.addIceCandidate({ candidate: null });
        //await this.pc.addIceCandidate(null);
    }

    close() {
        if (this.dataChannel) {
            try { this.sendCallEnded(); }
            catch { /* swallow error */ }
            try { this.dataChannel.close(); }
            catch { /* swallow error */ }
            this.dataChannel = null;
        }
        
        if (this.pc) {
            try { this.pc.close(); }
            catch { /* swallow error */ }
            this.pc = null;
        }
    }
}